import { App as CapApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { useCallback, useEffect, useRef, useState } from "react";

const WARNING_MS = 13 * 60 * 1000; // 13 minutes — show warning
const TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes — force logout
const COUNTDOWN_TOTAL = Math.floor((TIMEOUT_MS - WARNING_MS) / 1000); // 120 seconds
const THROTTLE_MS = 1000; // Throttle activity events to 1/sec

interface UseSessionTimeoutOptions {
	onWarning: () => void;
	onTimeout: () => void;
	enabled: boolean;
}

interface UseSessionTimeoutReturn {
	isWarning: boolean;
	countdown: number;
	resetTimer: () => void;
}

export function useSessionTimeout({
	onWarning,
	onTimeout,
	enabled,
}: UseSessionTimeoutOptions): UseSessionTimeoutReturn {
	const [isWarning, setIsWarning] = useState(false);
	const [countdown, setCountdown] = useState(COUNTDOWN_TOTAL);

	// Refs for callbacks to avoid re-registering DOM listeners
	const onWarningRef = useRef(onWarning);
	onWarningRef.current = onWarning;
	const onTimeoutRef = useRef(onTimeout);
	onTimeoutRef.current = onTimeout;

	// Timer refs
	const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const timeoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
		null,
	);
	const lastActivityRef = useRef(Date.now());
	const backgroundTimestampRef = useRef<number | null>(null);
	const isWarningRef = useRef(false);

	const clearAllTimers = useCallback(() => {
		if (warningTimerRef.current) {
			clearTimeout(warningTimerRef.current);
			warningTimerRef.current = null;
		}
		if (timeoutTimerRef.current) {
			clearTimeout(timeoutTimerRef.current);
			timeoutTimerRef.current = null;
		}
		if (countdownIntervalRef.current) {
			clearInterval(countdownIntervalRef.current);
			countdownIntervalRef.current = null;
		}
	}, []);

	const startCountdown = useCallback(() => {
		setCountdown(COUNTDOWN_TOTAL);
		countdownIntervalRef.current = setInterval(() => {
			setCountdown((prev) => {
				if (prev <= 1) {
					if (countdownIntervalRef.current) {
						clearInterval(countdownIntervalRef.current);
						countdownIntervalRef.current = null;
					}
					return 0;
				}
				return prev - 1;
			});
		}, 1000);
	}, []);

	const startTimers = useCallback(() => {
		clearAllTimers();
		lastActivityRef.current = Date.now();

		warningTimerRef.current = setTimeout(() => {
			isWarningRef.current = true;
			setIsWarning(true);
			onWarningRef.current();
			startCountdown();

			timeoutTimerRef.current = setTimeout(() => {
				onTimeoutRef.current();
			}, TIMEOUT_MS - WARNING_MS);
		}, WARNING_MS);
	}, [clearAllTimers, startCountdown]);

	const resetTimer = useCallback(() => {
		isWarningRef.current = false;
		setIsWarning(false);
		setCountdown(COUNTDOWN_TOTAL);
		startTimers();
	}, [startTimers]);

	// Handle background/foreground transitions
	const handleResume = useCallback(
		(_backgroundStartTime: number) => {
			const totalIdle = Date.now() - lastActivityRef.current;

			if (totalIdle >= TIMEOUT_MS) {
				// Exceeded timeout while backgrounded
				clearAllTimers();
				onTimeoutRef.current();
			} else if (totalIdle >= WARNING_MS) {
				// In warning zone — show warning with adjusted countdown
				clearAllTimers();
				const remaining = Math.max(
					0,
					Math.floor((TIMEOUT_MS - totalIdle) / 1000),
				);
				isWarningRef.current = true;
				setIsWarning(true);
				setCountdown(remaining);
				onWarningRef.current();

				// Start countdown from adjusted value
				countdownIntervalRef.current = setInterval(() => {
					setCountdown((prev) => {
						if (prev <= 1) {
							if (countdownIntervalRef.current) {
								clearInterval(countdownIntervalRef.current);
								countdownIntervalRef.current = null;
							}
							onTimeoutRef.current();
							return 0;
						}
						return prev - 1;
					});
				}, 1000);
			} else {
				// Still within safe zone — restart timers with adjusted time
				clearAllTimers();
				const remainingWarning = WARNING_MS - totalIdle;

				warningTimerRef.current = setTimeout(() => {
					isWarningRef.current = true;
					setIsWarning(true);
					onWarningRef.current();
					startCountdown();

					timeoutTimerRef.current = setTimeout(() => {
						onTimeoutRef.current();
					}, TIMEOUT_MS - WARNING_MS);
				}, remainingWarning);
			}
		},
		[clearAllTimers, startCountdown],
	);

	useEffect(() => {
		if (!enabled) {
			clearAllTimers();
			setIsWarning(false);
			return;
		}

		// Start timers on mount
		startTimers();

		// Throttled activity handler
		let lastThrottle = 0;
		const handleActivity = () => {
			// Don't reset if warning is showing — user must click "Stay Logged In"
			if (isWarningRef.current) return;

			const now = Date.now();
			if (now - lastThrottle < THROTTLE_MS) return;
			lastThrottle = now;
			lastActivityRef.current = now;
			startTimers();
		};

		const events: Array<keyof DocumentEventMap> = [
			"mousemove",
			"mousedown",
			"keydown",
			"touchstart",
			"scroll",
		];

		for (const event of events) {
			document.addEventListener(event, handleActivity, { passive: true });
		}

		// Web: visibilitychange for background tab detection
		const handleVisibilityChange = () => {
			if (document.hidden) {
				backgroundTimestampRef.current = Date.now();
			} else if (backgroundTimestampRef.current) {
				handleResume(backgroundTimestampRef.current);
				backgroundTimestampRef.current = null;
			}
		};
		document.addEventListener("visibilitychange", handleVisibilityChange);

		// Mobile (Capacitor): appStateChange for background detection
		let capListenerCleanup: { remove: () => void } | undefined;
		if (Capacitor.isNativePlatform()) {
			CapApp.addListener("appStateChange", ({ isActive }) => {
				if (!isActive) {
					backgroundTimestampRef.current = Date.now();
				} else if (backgroundTimestampRef.current) {
					handleResume(backgroundTimestampRef.current);
					backgroundTimestampRef.current = null;
				}
			}).then((listener) => {
				capListenerCleanup = listener;
			});
		}

		return () => {
			clearAllTimers();
			for (const event of events) {
				document.removeEventListener(event, handleActivity);
			}
			document.removeEventListener("visibilitychange", handleVisibilityChange);
			if (capListenerCleanup) {
				capListenerCleanup.remove();
			}
		};
	}, [enabled, clearAllTimers, startTimers, handleResume]);

	return { isWarning, countdown, resetTimer };
}
